import type { Pinia } from 'pinia';

// Frontend/general modules
import { useGeneral } from './general';
import { useCountries } from './countries';

// Backend modules
import { useArticleStatuses } from './article-statuses';
import { useArticles } from './articles';
import { useArticleFilters } from './article-filters';
import { useAgentChat } from './agent-chat';
import { useBrandUsers } from './brand-users';
import { useCertificates } from './certificates';
import { useCertificateReminders } from './certificate-reminders';
import { useChannels } from './channels';
import { useChat } from './chat';
import { useColorGroups } from './color-groups';
import { useColorStatuses } from './color-statuses';
import { useColorTypes } from './color-types';
import { useColorPaletteStatuses } from './color-palette-statuses';
import { useColors } from './colors';
import { useCompanies } from './companies';
import { useCompliances } from './compliances';
import { useComponentAttributeTypes } from './component-attribute-types';
import { useComponentStatuses } from './component-statuses';
import { useComponentTags } from './component-tags';
import { useComponentTypes } from './component-types';
import { useCurrencies } from './currencies';
import { useCustomFields } from './custom-fields';
import { useCustomFieldGroups } from '@/store/custom-field-groups.ts';
import { useLookupTables } from './lookup-tables';
import { useDeliveryTerms } from './delivery-terms';
import { useDrops } from './drops';
import { useMaterialDetailTypes } from './material-detail-types';
import { useMaterials } from './materials';
import { useMeasurements } from './measurements';
import { useMChartStatuses } from '@/store/mchart-statuses';
import { useMChartTags } from '@/store/mchart-tags';
import { usePlacementTags } from '@/store/placement-tags';
import { useProductImageTags } from '@/store/product-image-tags';
import { useModeOfTransports } from './mode-of-transports';
import { useDestinations } from './destinations';
import { useTableViews } from './table-views';
import { useNotifications } from './notifications';
import { useOrderStatuses } from '@/store/order-statuses';
import { useOrderTags } from '@/store/order-tags';
import { useOrders } from './orders';
import { useOrderDeliveries } from './order-deliveries';
import { useProductCategories } from './product-categories';
import { useProducts } from './products';
import { useSampleRoundDetails } from './sample-round-details';
import { useSampleRoundTypes } from './sample-round-types';
import { useSampleRounds } from './sample-rounds';
import { useSampleStatuses } from './sample-statuses';
import { useMeasurementStatuses } from './measurement-statuses';
import { useSampleTable } from './sample-table';
import { useSearch } from './search';
import { useRoles } from './roles';
import { useSeasons } from './seasons';
import { useTimelines } from './timelines';
import { useSectionNames } from './section-names';
import { usePlacementNames } from './placement-names';
import { useSizeConversionTables } from './size-conversion-tables';
import { useSizeSystems } from './size-systems';
import { useSuppliers } from './suppliers';
import { useSupplyChainStepTypes } from './supply-chain-step-types';
import { useSupplierStatuses } from '@/store/supplier-statuses.ts';
import { useTags } from './tags';
import { useTariffCodeSystems } from './tariff-code-systems';
import { useTreatmentTypes } from '@/store/treatment-types';
import { useTreatments } from './treatments';
import { useUnits } from './units';
import { useUser } from './user';
import { useUserConfig } from './user-config';
import { useVats } from './vats';
import { useFreights } from './freights';
import { useMarkupTargets } from './markup-targets';
import { useWebhooks } from './webhooks';
import { useWeights } from './weights';
import { useOrderTypes } from '@/store/order-types';
import { useCurrencyExchangeRates } from '@/store/currency-exchange-rates';
import { inject } from 'vue';
import { usePaymentTerms } from '@/store/payment-terms';
import { useSkuCodes } from '@/store/sku-codes';
import { useSkuCodeRanges } from '@/store/sku-code-ranges';
import { useHierarchy } from '@/store/hierarchy.ts';
import { useHierarchyLevels } from '@/store/hierarchy-levels.ts';
import { usePermissions } from '@/store/permissions.ts';
import { useBrand } from '@/store/brand.ts';
import { useActiveSeason } from '@/store/active-season.ts';
import { useOutPriceConfigs } from '@/store/out-price-configs.ts';
import { useInPriceConfigs } from '@/store/in-price-configs.ts';
import { useArticleTable } from './article-table';
import { useMChartTable } from './mchart-table';
import { useArticleCards } from './article-cards';
import { useComponents } from './components';
import { useAzureBlobFiles } from './azure-blob-files';

export type Stores = ReturnType<typeof definePiniaStores>;

export function definePiniaStores(pinia?: Pinia) {
  return {
    // Frontend/general
    countries: useCountries(pinia),
    general: useGeneral(pinia),
    activeSeason: useActiveSeason(pinia),

    // Backend
    agentChat: useAgentChat(pinia),
    articleCard: useArticleCards(pinia),
    articles: useArticles(pinia),
    articleStatuses: useArticleStatuses(pinia),
    articleTable: useArticleTable(pinia),
    azureBlobFiles: useAzureBlobFiles(pinia),
    articleFilters: useArticleFilters(pinia),
    brand: useBrand(pinia),
    brandUsers: useBrandUsers(pinia),
    certificates: useCertificates(pinia),
    certificateReminders: useCertificateReminders(pinia),
    channels: useChannels(pinia),
    chat: useChat(pinia),
    colorGroups: useColorGroups(pinia),
    colorStatuses: useColorStatuses(pinia),
    colorTypes: useColorTypes(pinia),
    colorPaletteStatuses: useColorPaletteStatuses(pinia),
    colors: useColors(pinia),
    companies: useCompanies(pinia),
    compliances: useCompliances(pinia),
    componentAttributeTypes: useComponentAttributeTypes(pinia),
    componentStatuses: useComponentStatuses(pinia),
    componentTags: useComponentTags(pinia),
    components: useComponents(pinia),
    componentTypes: useComponentTypes(pinia),
    currencies: useCurrencies(pinia),
    currencyExchangeRates: useCurrencyExchangeRates(pinia),
    customFields: useCustomFields(pinia),
    customFieldGroups: useCustomFieldGroups(pinia),
    lookupTables: useLookupTables(pinia),
    deliveryTerms: useDeliveryTerms(pinia),
    drops: useDrops(pinia),
    hierarchy: useHierarchy(pinia),
    hierarchyLevels: useHierarchyLevels(pinia),
    materialDetailTypes: useMaterialDetailTypes(pinia),
    materials: useMaterials(pinia),
    measurements: useMeasurements(pinia),
    mChartStatuses: useMChartStatuses(pinia),
    mChartTags: useMChartTags(pinia),
    mChartTable: useMChartTable(pinia),
    placementTags: usePlacementTags(pinia),
    productImageTags: useProductImageTags(pinia),
    modeOfTransports: useModeOfTransports(pinia),
    destinations: useDestinations(pinia),
    tableViews: useTableViews(pinia),
    notifications: useNotifications(pinia),
    orderStatuses: useOrderStatuses(pinia),
    orderTypes: useOrderTypes(pinia),
    orderTags: useOrderTags(pinia),
    orders: useOrders(pinia),
    orderDeliveries: useOrderDeliveries(pinia),
    outPriceConfigs: useOutPriceConfigs(pinia),
    inPriceConfigs: useInPriceConfigs(pinia),
    paymentTerms: usePaymentTerms(pinia),
    permissions: usePermissions(pinia),
    productCategories: useProductCategories(pinia),
    products: useProducts(pinia),
    sampleRoundDetails: useSampleRoundDetails(pinia),
    sampleRounds: useSampleRounds(pinia),
    sampleRoundTypes: useSampleRoundTypes(pinia),
    sampleStatuses: useSampleStatuses(pinia),
    measurementStatuses: useMeasurementStatuses(pinia),
    sampleTable: useSampleTable(pinia),
    skuCodes: useSkuCodes(pinia),
    skuCodeRanges: useSkuCodeRanges(pinia),
    search: useSearch(pinia),
    roles: useRoles(pinia),
    seasons: useSeasons(pinia),
    timelines: useTimelines(pinia),
    sectionNames: useSectionNames(pinia),
    placementNames: usePlacementNames(pinia),
    sizeConversionTables: useSizeConversionTables(pinia),
    sizeSystems: useSizeSystems(pinia),
    suppliers: useSuppliers(pinia),
    supplierStatuses: useSupplierStatuses(pinia),
    supplyChainStepTypes: useSupplyChainStepTypes(pinia),
    tags: useTags(pinia),
    tariffCodeSystems: useTariffCodeSystems(pinia),
    treatmentTypes: useTreatmentTypes(pinia),
    treatments: useTreatments(pinia),
    units: useUnits(pinia),
    user: useUser(pinia),
    userConfiguration: useUserConfig(pinia),
    vats: useVats(pinia),
    freights: useFreights(pinia),
    markupTargets: useMarkupTargets(pinia),
    webhooks: useWebhooks(pinia),
    weights: useWeights(pinia),
  };
}

export const useStores = () => inject<Stores>('$store')!;

// These should always be loaded for the PLM while logged in, needed on all pages.
// `skipNames` omits stores the caller has already loaded from the network in this boot —
// see app.js, where the warm-boot path loads the unseeded stores in the foreground before
// this sweep revalidates the seeded ones.
export async function loadAllGlobalStores(stores: Stores, skipNames: string[] = []) {
  const skip = new Set(skipNames);
  const loadableStores = Object.entries(stores)
    .filter(([name]) => !skip.has(name))
    //@ts-ignore can't type this easily
    .filter(([, value]) => typeof value === 'object' && typeof value.loadAll === 'function')
    .map(([, value]) => value);

  //@ts-ignore can't type this easily
  const results = await Promise.allSettled(loadableStores.map((store) => store.loadAll()));

  // A single transient failure among ~60 parallel requests shouldn't strand the whole
  // boot — retry just the failed stores once before giving up.
  const failedStores = loadableStores.filter((_, i) => results[i].status === 'rejected');
  if (failedStores.length > 0) {
    //@ts-ignore can't type this easily
    const retried = await Promise.allSettled(failedStores.map((store) => store.loadAll()));
    const stillFailing = retried.filter((r) => r.status === 'rejected');
    if (stillFailing.length > 0) {
      throw (stillFailing[0] as PromiseRejectedResult).reason;
    }
  }

  stores.user.fullyLoaded = true;
}

// Starts all SignalR listening for the global stores. (Needs to be called client-side)
// Idempotent: called from app.js (initial mount) and login flows. Calling .listen() twice
// would register every signalR handler twice on the same connection, doubling every
// SignalR-driven side effect (e.g. articles.ts:listen() would fire two article_GetArticle
// calls per ArticlesUpdated broadcast).
let globalListenersAttached = false;
export function listenAllGlobalStores(stores: Stores) {
  if (globalListenersAttached) return;
  globalListenersAttached = true;

  for (const [, value] of Object.entries(stores)) {
    //@ts-ignore can't type this easily
    if (typeof value === 'object' && typeof value.listen === 'function') {
      //@ts-ignore can't type this easily
      value.listen();
    }
  }
}
